import { ActivatedRouteSnapshot, Resolve, RouterStateSnapshot } from '@angular/router';

import { forkJoin, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { of } from 'rxjs/internal/observable/of';

import {
    ApiError,
    ApiResponse,
    ServerResponse,
    ServerResponseData,
    HttpStatusCodesEnum,
    TradingGroupStatusesEnum,
    TradingGroupBehaviorTypesEnum,
    TradingService,
    TradingGroupInterface,
    TradingGroupModel,
    TradingGroupSearchModel,
    CurrencyModel,
    LookupService,
    CurrencyInterface,
} from '@spa/api';

import { InArrayValidator } from '@spa/validators';

import { Helper } from '../../../shared/classes';
import { Pagination } from '../../../shared/components';

import { ClientTypesEnum, PanelAccountsAddCashResolverErrorStatesEnum } from '../enums';

import {
    AccountCashAccountIdSelector,
    AccountClientIdSelector,
    AccountClientTypeSelector,
    TradingAccountsCashControls
} from 'appAssets/descriptors';

export abstract class AccountsAddCashTradingResolver<ResolverType> implements Resolve<ServerResponse<ResolverType>> {

    constructor(
        protected readonly tradingService: TradingService,
        protected readonly lookupService: LookupService
    ) {
    }

    private getTradingGroupSearchModel(clientType: number, clientId: number): TradingGroupSearchModel[] {
        let behaviorTypes = [];

        const speciallyTypes = [
            TradingGroupBehaviorTypesEnum.SpeciallyForCompany,
            TradingGroupBehaviorTypesEnum.SpeciallyForIndividual
        ];

        if (clientType !== ClientTypesEnum.Individual) {
            behaviorTypes = [
                TradingGroupBehaviorTypesEnum.SpeciallyForCompany,
                TradingGroupBehaviorTypesEnum.ForAllCompanies
            ];
        } else {
            behaviorTypes = [
                TradingGroupBehaviorTypesEnum.SpeciallyForIndividual,
                TradingGroupBehaviorTypesEnum.ForAllIndividuals
            ];
        }

        return behaviorTypes.map((type: number) => {
            return new TradingGroupSearchModel(
                null,
                TradingGroupStatusesEnum.Active,
                null,
                null,
                null,
                [type],
                (speciallyTypes.indexOf(type) < 0) ? null : clientId,
                null,
                null,
                null,
                null,
                null,
                null,
                Pagination.defaultPagesChunk,
                Pagination.calculateItemsPerPagesChunk(),
                TradingGroupSearchModel.getIdSelector(),
                false,
                false
            );
        });
    }

    private getTradingGroups(search: TradingGroupSearchModel): Observable<ApiError | TradingGroupModel[]> {
        return this.tradingService.searchGroup(search)
            .pipe(
                map((response: ServerResponseData<TradingGroupInterface[]>) => {
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        return new ApiError(
                            PanelAccountsAddCashResolverErrorStatesEnum.GetTradingGroupsFailed
                        );
                    }

                    if (Helper.errorsHelper().checkForInvalidRespondedArray(response)) {
                        return [];
                    }

                    return (response.data as TradingGroupInterface[])
                        .map((item: TradingGroupInterface) => new TradingGroupModel(item));
                })
            );
    }

    private getCurrencies(): Observable<ApiError | CurrencyModel[]> {
        return this.lookupService.getAllCurrencies()
            .pipe(
                map((response: ServerResponseData<CurrencyInterface[]>) => {
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        return new ApiError(
                            PanelAccountsAddCashResolverErrorStatesEnum.GetCurrenciesNotFound
                        );
                    }

                    if (Helper.errorsHelper().checkForInvalidRespondedArray(response)) {
                        return [];
                    }

                    return (response.data as CurrencyInterface[])
                        .map((item: CurrencyInterface) => new CurrencyModel(item));
                })
            );
    }


    abstract getDataAfterTradingGroups(
        clientId: number,
        clientType: number,
        cashAccountId: number,
        tradingGroups: TradingGroupModel[],
        currencies: CurrencyModel[]
    ): ServerResponse<ResolverType>;

    resolve(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): ServerResponse<ResolverType> {
        let cashAccountId = null;
        const clientType = parseInt(route.parent.paramMap.get(AccountClientTypeSelector), 10);
        const clientId = parseInt(route.parent.paramMap.get(AccountClientIdSelector), 10);

        if (route.parent.paramMap.get(AccountCashAccountIdSelector)) {
            cashAccountId = parseInt(route.parent.paramMap.get(AccountCashAccountIdSelector), 10);
        }

        return this.load(clientType, clientId, cashAccountId);
    }

    /** The page's data for a client, outside the router: the account onboarding wizard loads its steps with it. */
    load(clientType: number, clientId: number, cashAccountId: number = null): ServerResponse<ResolverType> {
        return this.loadWithGroupsAndCurrencies(clientType, clientId, (tradingGroups: TradingGroupModel[], currencies: CurrencyModel[]) =>
            this.getDataAfterTradingGroups(clientId, clientType, cashAccountId, tradingGroups, currencies));
    }

    /** Loads the client's active trading groups and the currencies, then hands them to `next`. */
    protected loadWithGroupsAndCurrencies(
        clientType: number,
        clientId: number,
        next: (tradingGroups: TradingGroupModel[], currencies: CurrencyModel[]) => ServerResponse<ResolverType>
    ): ServerResponse<ResolverType> {
        const requestsStack = [];
        this.getTradingGroupSearchModel(
            clientType,
            clientId
        ).forEach((search: TradingGroupSearchModel) => {
            requestsStack.push(this.getTradingGroups(search));
        });
        requestsStack.push(this.getCurrencies());

        return forkJoin(requestsStack).pipe(
            switchMap((responses: (ApiError | TradingGroupModel[] | CurrencyModel[])[]) => {
                if ((responses[0] instanceof ApiError) || (responses[1] instanceof ApiError) || (responses[2] instanceof ApiError)) {
                    const errors = responses.filter((response: (ApiError | TradingGroupModel[] | CurrencyModel[])) => {
                        return response instanceof ApiError;
                    }) as ApiError[];

                    const badRequestError = new ApiResponse(HttpStatusCodesEnum.BadRequest, errors);

                    return Helper.errorsHelper().createErrorResponseObservable(
                        badRequestError
                    );
                }

                const firstTradingGroupsChunk = responses[0] as TradingGroupModel[];
                const secondTradingGroupsChunk = responses[1] as TradingGroupModel[];
                const currencies = responses[2] as CurrencyModel[];
                const tradingGroups = firstTradingGroupsChunk.concat(...secondTradingGroupsChunk);

                if (tradingGroups.length === 0) {
                    const noDataError = Helper.errorsHelper().createResponseForNoData(
                        PanelAccountsAddCashResolverErrorStatesEnum.ActiveTradingGroupsNotFound
                    );

                    return of(noDataError);
                }
                if (currencies.length === 0) {
                    const noDataError = Helper.errorsHelper().createResponseForNoData(
                        PanelAccountsAddCashResolverErrorStatesEnum.GetCurrenciesNotFound
                    );

                    return of(noDataError);
                }

                return next(tradingGroups, currencies);
            })
        );
    }
}
